'use client';

import type { ChatRef } from '@terra/ui-design-system';
import {
  ChatCredentialsProvider,
  IconButton,
  MetaDefault,
  Tooltip,
} from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Add01Icon, Cancel01Icon } from '@terra/ui-icons';
import { useDetachedWindows } from '../hooks/useDetachedWindows';
import { useTranslations } from 'next-intl';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useChatStore } from '../store/chatStore';
import { useMentions } from '../hooks/useMentions';
import {
  shouldMarkComposerMentionsCleared,
  shouldSkipStoreMentionsSync,
} from '../hooks/composerSendContext';
import {
  useConversationsStore,
  useSubscribeToConversations,
} from '../store/conversations-store';
import { useChatTabs } from '../store/chatTabsStore';
import { useTargetScope, useOptionalTargetId } from '../store/projectStore';
import {
  ConversationHistoryPanel,
  ConversationHistoryTrigger,
  useConversationHistoryData,
} from './ConversationHistory';
import {
  MentionsMenuComposer,
  type MentionsMenuComposerRef,
} from './mentions/MentionsMenuComposer';
import { ChatInstance } from './chat/ChatInstance';
import { CoPilotChatOverflowMenu } from './chat/CoPilotChatOverflowMenu';
import { useCreateNewChatTab } from './chat/actions/useCreateNewChatTab';
import { ChatTabs } from './chat/tabs/ChatTabs';
import { getDetachedWindowMode, isDetachedWindow } from '../utils/detachedMode';
import { isCrossWindowSyncReady } from '../store/crossWindowSync';
import { ChatFindingSearchWrapper } from './chat/findings/ChatFindingSearchWrapper';

function CopilotHeaderIconSlot({ children }: { children: ReactNode }) {
  return (
    <Box
      w="7"
      h="7"
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
    >
      {children}
    </Box>
  );
}

function CopilotHeaderCloseButton({
  onMinimize,
  label,
}: {
  onMinimize: () => void;
  label: string;
}) {
  return (
    <Tooltip title={label} positioning={{ placement: 'bottom' }}>
      <IconButton
        variant="ghost"
        size="xs"
        onClick={onMinimize}
        aria-label={label}
        _hover={{
          color: 'fg.default',
          bg: 'transparent',
        }}
      >
        <Cancel01Icon size={16} />
      </IconButton>
    </Tooltip>
  );
}

export interface CoPilotChatProps {
  onMinimize?: () => void;
  hideHeader?: boolean;
  isHistoryOpen?: boolean;
  onHistoryOpenChange?: (isOpen: boolean) => void;
}
export interface CoPilotChatHandle {
  newChat: () => void;
}
const CoPilotChat = forwardRef<CoPilotChatHandle, CoPilotChatProps>(
  (
    {
      onMinimize,
      hideHeader = false,
      isHistoryOpen: isHistoryOpenProp,
      onHistoryOpenChange,
    },
    ref,
  ) => {
    const chatComponentRef = useRef<ChatRef>(null);
    const mentionsMenuRef = useRef<MentionsMenuComposerRef>(null);
    const tabsContainerRef = useRef<HTMLDivElement>(null);
    const [uncontrolledHistoryOpen, setUncontrolledHistoryOpen] =
      useState(false);
    const isHistoryOpenControlled = isHistoryOpenProp !== undefined;
    const isHistoryOpen = isHistoryOpenControlled
      ? isHistoryOpenProp
      : uncontrolledHistoryOpen;
    const conversationHistory = useConversationHistoryData();
    const setHistoryOpen = useCallback(
      (isOpen: boolean) => {
        if (!isHistoryOpenControlled) {
          setUncontrolledHistoryOpen(isOpen);
        }

        onHistoryOpenChange?.(isOpen);
      },
      [isHistoryOpenControlled, onHistoryOpenChange],
    );

    useEffect(() => {
      return () => {
        onHistoryOpenChange?.(false);
      };
    }, [onHistoryOpenChange]);
    const { targetType, hasTarget } = useTargetScope();
    const targetId = useOptionalTargetId();
    const setActiveConversation = useConversationsStore(
      (state) => state.setActiveConversation,
    );
    const t = useTranslations('proxy.detachedWindows');
    const tLayout = useTranslations('layout');
    const tCopilot = useTranslations('chat.copilot');
    const { detachCopilot, reattachCopilot, isCopilotDetached } =
      useDetachedWindows();
    const isDetachedCopilotWindow = getDetachedWindowMode().type === 'copilot';
    const hasDetachedWindowsApi = !!window.electronAPI?.detachedWindows;
    const isCopilotPoppedOutInMain =
      isCopilotDetached && !isDetachedCopilotWindow;
    const showPopOut =
      hasDetachedWindowsApi && !isDetachedCopilotWindow && !isCopilotDetached;
    const showBringBack =
      hasDetachedWindowsApi && (isDetachedCopilotWindow || isCopilotDetached);
    const {
      chatTabs,
      activeChatTabId,
      activeChatTab,
      updateChatTab,
      setActiveChatTabId,
    } = useChatTabs();
    const exportTargetId = activeChatTab?.targetId ?? targetId;
    // Register chat ref in store
    const { setChatRef, setAddMentionFn } = useChatStore();

    useSubscribeToConversations();
    const { selectedMentions, clearMentions, addMention, setSelectedMentions } =
      useMentions();

    // Register chat ref and addMention function in store for external access
    useEffect(() => {
      setChatRef(chatComponentRef as React.RefObject<ChatRef | null>);
      setAddMentionFn(addMention);

      return () => {
        setAddMentionFn(null);
      };
    }, [setChatRef, setAddMentionFn, chatComponentRef, addMention]);
    const previousActiveChatTabIdRef = useRef<string | null>(null);
    const lastSyncedMentionsKeyRef = useRef<string | null>(null);
    const composerMentionsClearedRef = useRef(false);
    const pendingExternalSyncRef = useRef(false);
    const mentionsKey = useMemo(
      () => JSON.stringify(activeChatTab?.mentions ?? []),
      [activeChatTab?.mentions],
    );
    const serializedSelectedMentions = useMemo(
      () =>
        selectedMentions.map((mention) => ({
          id: mention.id,
          label: mention.label,
          type: mention.type,
          mentionEntity: mention.mentionEntity,
        })),
      [selectedMentions],
    );
    const selectedMentionsKey = useMemo(
      () => JSON.stringify(serializedSelectedMentions),
      [serializedSelectedMentions],
    );

    useEffect(() => {
      const tabChanged = activeChatTabId !== previousActiveChatTabIdRef.current;

      if (
        shouldSkipStoreMentionsSync({
          clearedLocally: composerMentionsClearedRef.current,
          tabChanged,
          storeMentionsKey: mentionsKey,
        })
      ) {
        return;
      }

      composerMentionsClearedRef.current = false;
      const externalChanged = mentionsKey !== lastSyncedMentionsKeyRef.current;
      const localDivergedFromLastSync =
        selectedMentionsKey !== lastSyncedMentionsKeyRef.current;
      const shouldSyncFromStore =
        tabChanged || (externalChanged && !localDivergedFromLastSync);

      if (!shouldSyncFromStore) {
        return;
      }

      previousActiveChatTabIdRef.current = activeChatTabId;
      lastSyncedMentionsKeyRef.current = mentionsKey;
      pendingExternalSyncRef.current = true;
      if (activeChatTab?.mentions && activeChatTab.mentions.length > 0) {
        setSelectedMentions(
          activeChatTab.mentions.map((mention) => ({
            id: mention.id,
            label: mention.label,
            type: mention.type,
            mentionEntity: mention.mentionEntity,
          })),
        );
      } else if (activeChatTabId) {
        clearMentions();
      }
    }, [
      activeChatTabId,
      mentionsKey,
      selectedMentionsKey,
      activeChatTab,
      setSelectedMentions,
      clearMentions,
    ]);
    useEffect(() => {
      if (!activeChatTabId) {
        return;
      }

      if (pendingExternalSyncRef.current) {
        pendingExternalSyncRef.current = false;
        if (serializedSelectedMentions.length > 0) {
          return;
        }
      }

      if (selectedMentionsKey === lastSyncedMentionsKeyRef.current) {
        return;
      }

      const previousMentionsKey = lastSyncedMentionsKeyRef.current;

      if (
        shouldMarkComposerMentionsCleared({
          selectedMentionsKey,
          previousMentionsKey,
          storeMentionsKey: mentionsKey,
        })
      ) {
        composerMentionsClearedRef.current = true;
      }

      lastSyncedMentionsKeyRef.current = selectedMentionsKey;
      if (selectedMentionsKey === mentionsKey) {
        return;
      }

      updateChatTab(activeChatTabId, {
        mentions: serializedSelectedMentions,
      });
    }, [
      activeChatTabId,
      mentionsKey,
      selectedMentionsKey,
      serializedSelectedMentions,
      updateChatTab,
    ]);
    const { createNewChatTab } = useCreateNewChatTab();
    const handleNewChat = () => {
      createNewChatTab({ contentContainerRef: tabsContainerRef });
    };

    useEffect(() => {
      if (isCopilotPoppedOutInMain) {
        return;
      }

      if (isDetachedWindow() && !isCrossWindowSyncReady()) {
        return;
      }

      if (chatTabs.length === 0) {
        const timeoutId = setTimeout(() => {
          createNewChatTab({
            contentContainerRef: tabsContainerRef,
          });
        }, 50);

        return () => clearTimeout(timeoutId);
      }

      const activeTabBelongsToApp = chatTabs.some(
        (tab) => tab.tabId === activeChatTabId,
      );

      if (!activeTabBelongsToApp) {
        const lastTab = chatTabs.at(-1);

        if (lastTab) {
          setActiveChatTabId(lastTab.tabId);
          if (lastTab.conversationId) {
            setActiveConversation(lastTab.conversationId);
          }
        }
      }
    }, [
      chatTabs,
      chatTabs.length,
      targetId,
      activeChatTabId,
      createNewChatTab,
      setActiveChatTabId,
      setActiveConversation,
      isCopilotPoppedOutInMain,
    ]);
    useImperativeHandle(ref, () => ({
      newChat: handleNewChat,
    }));

    return (
      <ChatCredentialsProvider>
        <ChatFindingSearchWrapper targetId={targetId} targetType={targetType}>
          <Box
            h="full"
            w="full"
            bg="bg.subtle"
            display="flex"
            flexDirection="column"
            position="relative"
            overflow="hidden"
          >
            <VStack
              gap="0"
              h="full"
              alignItems="stretch"
              overflow="hidden"
              flex="1"
              w="full"
              minW={0}
            >
              {!hideHeader && (
                <Box
                  h="8"
                  px="3"
                  bg="bg.default"
                  display="flex"
                  alignItems="center"
                  w="full"
                  flexShrink={0}
                >
                  <HStack
                    gap="2"
                    alignItems="center"
                    w="full"
                    overflow="hidden"
                    flex="1"
                  >
                    {!isCopilotPoppedOutInMain && (
                      <>
                        <HStack
                          ref={tabsContainerRef}
                          gap="1"
                          flex="1"
                          overflow="auto"
                          css={{
                            '&::-webkit-scrollbar': { display: 'none' },
                            scrollbarWidth: 'none',
                          }}
                        >
                          <ChatTabs contentContainerRef={tabsContainerRef} />
                        </HStack>

                        <HStack gap="0" alignItems="center" flexShrink={0}>
                          <CopilotHeaderIconSlot>
                            <IconButton
                              variant="ghost"
                              size="xs"
                              onClick={handleNewChat}
                              aria-label={tCopilot('newChatTitle')}
                              _hover={{
                                color: 'fg.default',
                                bg: 'transparent',
                              }}
                            >
                              <Add01Icon size={16} />
                            </IconButton>
                          </CopilotHeaderIconSlot>

                          <CopilotHeaderIconSlot>
                            <ConversationHistoryTrigger
                              isOpen={isHistoryOpen}
                              onOpenChange={setHistoryOpen}
                              labels={conversationHistory.labels}
                              hasActiveChats={
                                conversationHistory.hasActiveChats
                              }
                            />
                          </CopilotHeaderIconSlot>

                          <CopilotHeaderIconSlot>
                            <CoPilotChatOverflowMenu
                              targetId={exportTargetId}
                              threadId={activeChatTab?.chatId}
                              showPopOut={showPopOut}
                              showBringBack={showBringBack}
                              onPopOut={detachCopilot}
                              onBringBack={reattachCopilot}
                              disabled={!activeChatTab}
                            />
                          </CopilotHeaderIconSlot>

                          {onMinimize ? (
                            <CopilotHeaderIconSlot>
                              <CopilotHeaderCloseButton
                                onMinimize={onMinimize}
                                label={tLayout('closeCopilot')}
                              />
                            </CopilotHeaderIconSlot>
                          ) : null}
                        </HStack>
                      </>
                    )}

                    {isCopilotPoppedOutInMain && (
                      <>
                        <MetaDefault flex="1" color="fg.muted" truncate>
                          {t('copilotDetachedHint')}
                        </MetaDefault>
                        <HStack gap="0" alignItems="center" flexShrink={0}>
                          <CopilotHeaderIconSlot>
                            <CoPilotChatOverflowMenu
                              targetId={targetId}
                              showBringBack={showBringBack}
                              onBringBack={reattachCopilot}
                            />
                          </CopilotHeaderIconSlot>
                          {onMinimize ? (
                            <CopilotHeaderIconSlot>
                              <CopilotHeaderCloseButton
                                onMinimize={onMinimize}
                                label={tLayout('closeCopilot')}
                              />
                            </CopilotHeaderIconSlot>
                          ) : null}
                        </HStack>
                      </>
                    )}
                  </HStack>
                </Box>
              )}

              <Box position="relative" flex="1" overflow="hidden" w="full">
                {isCopilotPoppedOutInMain ? (
                  <VStack
                    h="full"
                    w="full"
                    alignItems="center"
                    justifyContent="center"
                    px="4"
                  >
                    <MetaDefault color="fg.muted" textAlign="center">
                      {t('copilotDetachedHint')}
                    </MetaDefault>
                  </VStack>
                ) : chatTabs.length > 0 ? (
                  chatTabs.map((tab) => (
                    <ChatInstance
                      key={tab.tabId}
                      tabId={tab.tabId}
                      chatId={tab.chatId}
                      conversationId={tab.conversationId}
                      isActive={tab.tabId === activeChatTabId}
                      skillDraftId={tab.skillDraftId}
                      linkedObject={tab.linkedObject}
                      draftInput={tab.draftInput}
                      chatRef={
                        tab.tabId === activeChatTabId
                          ? (chatComponentRef as React.RefObject<ChatRef | null>)
                          : undefined
                      }
                      mentionsMenuRef={
                        tab.tabId === activeChatTabId
                          ? (mentionsMenuRef as React.RefObject<MentionsMenuComposerRef | null>)
                          : undefined
                      }
                    />
                  ))
                ) : (
                  <VStack
                    h="full"
                    w="full"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <MetaDefault color="fg.muted">No chat selected</MetaDefault>
                  </VStack>
                )}
              </Box>
            </VStack>

            {isHistoryOpen ? (
              <Box
                position="absolute"
                top="0"
                right="0"
                bottom="0"
                zIndex="dropdown"
                boxShadow="lg"
              >
                <ConversationHistoryPanel
                  isOpen={isHistoryOpen}
                  onClose={() => setHistoryOpen(false)}
                  labels={conversationHistory.labels}
                  drafts={conversationHistory.drafts}
                  savedConversations={conversationHistory.savedConversations}
                  activeId={conversationHistory.activeId}
                  onSelect={conversationHistory.onSelect}
                  onDelete={conversationHistory.onDelete}
                  onRename={conversationHistory.onRename}
                />
              </Box>
            ) : null}

            {hasTarget && (
              <MentionsMenuComposer
                mode="inline"
                ref={mentionsMenuRef}
                getAnchorPosition={() =>
                  chatComponentRef.current?.getMentionsAnchorPosition()
                }
                onClose={() => {
                  setTimeout(() => {
                    chatComponentRef.current?.focus?.();
                  }, 100);
                }}
              />
            )}
          </Box>
        </ChatFindingSearchWrapper>
      </ChatCredentialsProvider>
    );
  },
);

CoPilotChat.displayName = 'CoPilotChat';

export { CoPilotChat };
